import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Cpu } from "lucide-react";

// Illustrative homepage schematic of the real 3-portal + multi-AI reconciliation.
// No dollar figures are shown (nothing fabricated) — bar widths are decorative only.
const PORTALS = [
  ["Zillow", "Live Zestimate® via Bridge Interactive", "Live", "72%"],
  ["Redfin", "Side-by-side in your valuation report", "Compare", "64%"],
  ["Homes.com", "Side-by-side in your valuation report", "Compare", "68%"],
];

export default function HomePortalDemo() {
  return (
    <section className="mx-auto max-w-7xl px-5 sm:px-8 py-16" data-testid="home-portal-demo">
      <span className="eyebrow">3-Portal Comparison</span>
      <h2 className="mt-2 font-serif text-3xl sm:text-4xl text-brand-ink">Every portal's number, side by side</h2>
      <p className="mt-3 max-w-2xl text-sm text-brand-stone leading-relaxed">
        Instead of trusting a single estimate, Equitas lines up the major portals next to a multi-model AI appraisal, then reconciles them into one defensible range for your home.
      </p>

      <div className="mt-8 grid gap-6 lg:grid-cols-[1fr_auto_1fr] items-center">
        <div className="grid sm:grid-cols-3 gap-4">
          {PORTALS.map(([name, desc, tag, width]) => (
            <div key={name} className="rounded-2xl bg-white border border-brand-hairline p-5" data-testid="portal-demo-card">
              <div className="flex items-center justify-between gap-2">
                <span className="font-serif text-lg text-brand-ink">{name}</span>
                <span className="rounded-full bg-brand-sand px-2 py-0.5 text-[0.6rem] font-semibold uppercase tracking-wider text-brand-stone">{tag}</span>
              </div>
              <p className="mt-2 text-xs text-brand-stone leading-relaxed">{desc}</p>
              <div className="mt-4 h-2 rounded-full bg-brand-sand overflow-hidden">
                <div className="h-full bg-brand-gold/70" style={{ width }} />
              </div>
            </div>
          ))}
        </div>

        <div className="hidden lg:flex items-center justify-center text-brand-stone">
          <ArrowRight className="h-7 w-7" />
        </div>

        <div className="rounded-2xl bg-brand-emerald text-brand-cream p-6" data-testid="portal-demo-reconciled">
          <Cpu className="h-6 w-6 text-brand-gold" />
          <p className="mt-3 font-serif text-2xl">Your reconciled range</p>
          <p className="mt-2 text-sm text-brand-cream/80 leading-relaxed">
            A 3-model AI panel (Gemini, OpenAI, Claude) weighs local comparables and blends the portal signals into a low–high range with the math shown — then applies local transfer taxes and your 1% net equity.
          </p>
          <Link to="/" data-testid="portal-demo-cta" className="mt-5 inline-flex items-center gap-2 rounded-xl bg-brand-cream px-5 py-3 text-sm font-semibold text-brand-emerald hover:bg-white transition-colors">
            Enter your address <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
      <p className="mt-3 text-xs text-brand-stone/60">Illustrative schematic — portal availability varies by home; enter your address for live figures.</p>
    </section>
  );
}
